import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { OverviewCards } from "@/components/OverviewCards";
import { BrandsTable } from "@/components/BrandsTable";
import { AgentFeed } from "@/components/AgentFeed";
import { FinancialChart } from "@/components/FinancialChart";
import { TerritoryMap } from "@/components/TerritoryMap";
import { SystemHealth } from "@/components/SystemHealth";
import { Topbar } from "@/components/Topbar";
import { AlertsPanel } from "@/components/AlertsPanel";
import { WompiPanel } from "@/components/WompiPanel";
import { PilotPanel } from "@/components/PilotPanel";
import { ProductPage } from "@/components/ProductPage";
import { MarketingPanel } from "@/components/MarketingPanel";
import { IntelligencePanel } from "@/components/IntelligencePanel";
import { AuditorPanel } from "@/components/AuditorPanel";
import { AuthProvider, ProtectedRoute, LoginPage, LandingPage, useAuth } from "@/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [brands, setBrands] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [agentLogs, setAgentLogs] = useState([]);
  const [financials, setFinancials] = useState([]);
  const [health, setHealth] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [wompiStatus, setWompiStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const timeout = { timeout: 8000 };
      const [ov, br, te, al, fi, he, alerts_r, ws, tx] = await Promise.all([
        axios.get(`${API}/overview`, timeout).catch(() => ({ data: overview })),
        axios.get(`${API}/brands`, timeout).catch(() => ({ data: brands })),
        axios.get(`${API}/territories`, timeout).catch(() => ({ data: territories })),
        axios.get(`${API}/agent-logs?limit=60`, timeout).catch(() => ({ data: agentLogs })),
        axios.get(`${API}/financials`, timeout).catch(() => ({ data: financials })),
        axios.get(`${API}/system-health`, timeout).catch(() => ({ data: health })),
        axios.get(`${API}/alerts`, timeout).catch(() => ({ data: [] })),
        axios.get(`${API}/wompi/status`, timeout).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/wompi/transactions`, timeout).catch(() => ({ data: [] })),
      ]);
      if (ov.data) setOverview(ov.data);
      if (br.data) setBrands(br.data);
      if (te.data) setTerritories(te.data);
      if (al.data) setAgentLogs(al.data);
      if (fi.data) setFinancials(fi.data);
      if (he.data) setHealth(he.data);
      if (alerts_r.data) setAlerts(alerts_r.data);
      if (ws.data) setWompiStatus(ws.data);
      if (tx.data) setTransactions(tx.data);
    } catch (e) {
      console.error("Error loading data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try { await axios.post(`${API}/seed`); } catch (_) {}
      fetchAll();
    };
    init();
    const interval = setInterval(fetchAll, 15000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handleResolveAlert = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/resolve`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) {
      console.error("Error resolving alert:", e);
    }
  };

  const handleRunAnalysis = async (type) => {
    setAnalysisLoading(true);
    try {
      if (type === "territories") {
        await axios.post(`${API}/ai/analyze-territories`);
      } else if (type === "geo") {
        await axios.post(`${API}/ai/geo-intelligence`, {}, { timeout: 60000 });
      } else {
        await axios.post(`${API}/ai/strategic-decision`);
      }
      const [alerts_r, logs_r, terr_r] = await Promise.all([
        axios.get(`${API}/alerts`).catch(() => ({ data: [] })),
        axios.get(`${API}/agent-logs?limit=60`),
        axios.get(`${API}/territories`).catch(() => ({ data: [] })),
      ]);
      setAlerts(alerts_r.data);
      setAgentLogs(logs_r.data);
      setTerritories(terr_r.data);
    } catch (e) {
      console.error("Analysis error:", e);
    } finally {
      setAnalysisLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="loading-screen" style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: "100vh", background: "#09090b", color: "#a1a1aa",
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
        letterSpacing: "0.15em", textTransform: "uppercase"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 24, color: "#fff", marginBottom: 8, fontFamily: "'Chivo', sans-serif" }}>GHOSTBRAND</div>
          Inicializando motor...
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ghostbrand-dashboard" style={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "auto" }}>
      <Topbar overview={overview} wompiStatus={wompiStatus} alerts={alerts} />

      <div style={{
        flex: 1, display: "grid",
        gridTemplateColumns: "300px 1fr 340px",
        gridTemplateRows: "1fr 320px",
        gap: 1,
        background: "rgba(255,255,255,0.04)",
        minHeight: 0,
        overflow: "auto"
      }}>
        {/* LEFT: Metrics + Territory + Alerts */}
        <div style={{ gridColumn: "1", gridRow: "1", display: "flex", flexDirection: "column", gap: 1, background: "rgba(255,255,255,0.04)", overflow: "auto" }}>
          <div className="panel" style={{ flex: "0 0 auto" }}>
            <OverviewCards overview={overview} />
          </div>
          <div className="panel" style={{ flex: "0 0 auto", minHeight: 140 }}>
            <TerritoryMap territories={territories} />
          </div>
          <div className="panel" style={{ flex: 1 }}>
            <AlertsPanel
              alerts={alerts}
              onResolve={handleResolveAlert}
              onRunAnalysis={handleRunAnalysis}
              analysisLoading={analysisLoading}
            />
          </div>
        </div>

        {/* CENTER: Brands table */}
        <div className="panel" style={{ gridColumn: "2", gridRow: "1", overflow: "auto" }}>
          <BrandsTable brands={brands} />
        </div>

        {/* RIGHT: Agent Feed + Pilot */}
        <div style={{ gridColumn: "3", gridRow: "1", display: "flex", flexDirection: "column", gap: 1, background: "rgba(255,255,255,0.04)", overflow: "auto" }}>
          <div className="panel" style={{ flex: 1 }}>
            <AgentFeed logs={agentLogs} />
          </div>
          <div className="panel" style={{ flex: "0 0 260px" }}>
            <PilotPanel onPilotComplete={fetchAll} />
          </div>
        </div>

        {/* BOTTOM ROW */}
        <div className="panel" style={{ gridColumn: "1", gridRow: "2", overflow: "auto" }}>
          <IntelligencePanel />
        </div>
        <div style={{ gridColumn: "2", gridRow: "2", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, overflow: "auto" }}>
          <div className="panel" style={{ overflow: "auto" }}>
            <WompiPanel wompiStatus={wompiStatus} transactions={transactions} />
          </div>
          <div className="panel" style={{ overflow: "auto" }}>
            <MarketingPanel />
          </div>
        </div>
        <div style={{ gridColumn: "3", gridRow: "2", display: "flex", flexDirection: "column", gap: 1, background: "rgba(255,255,255,0.04)", overflow: "auto" }}>
          <div className="panel" style={{ flex: 1, overflow: "auto" }}>
            <AuditorPanel />
          </div>
          <div className="panel" style={{ flex: "0 0 auto", overflow: "auto" }}>
            <SystemHealth health={health} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardWithLogout() {
  const { user, logout } = useAuth();
  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "fixed", top: 12, right: 160, zIndex: 1000, display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }} data-testid="dashboard-user-email">{user?.email}</span>
        <button
          data-testid="dashboard-logout"
          onClick={logout}
          style={{ padding: "6px 12px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171", borderRadius: 6, fontSize: 11, cursor: "pointer" }}
        >
          Logout
        </button>
      </div>
      <Dashboard />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardWithLogout /></ProtectedRoute>} />
          <Route path="/product/:productId" element={<ProductPage />} />
          <Route path="/product/:productId/gracias" element={<ProductPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
